import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import StarsIcon from '@material-ui/icons/Stars';
import LocalHospitalOutlinedIcon from '@material-ui/icons/LocalHospitalOutlined';
import GroupIcon from '@material-ui/icons/Group';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import VerifiedUser from '@material-ui/icons/VerifiedUser';

import ProviderName from 'icons/ProviderName';
import Place from 'icons/Place';
import Service from 'icons/Service';
import CostEfficiencyColored from 'icons/colored/CostEfficiencyColored';
import FocusAreasColored from 'icons/colored/FocusAreasColored';
import StrengthsColored from 'icons/colored/StrengthsColored';
import HealthAndWellness from 'icons/colored/HealthAndWellness';
import FacilityTypeColored from 'icons/colored/FacilityTypeColored';

import type {
  DefaultLocation,
  ProviderResultType,
  PlaceResultType,
  ResultType,
  DayOfTheWeekAbbreviation,
  FeaturedActionVariant,
} from 'types/common.types';
import type { SvgIconProps } from '@material-ui/core';
import type { ComponentType } from 'react';

/* ******************************
 * Embold client slugs - Front End defined client identifiers. No guarantee that it will have a matching backend value.
 ****************************** */
export const ACME = 'acme' as const;
export const AMAZON = 'amazon' as const;
export const AXA = 'axa' as const;
export const BCBS_NORTH_CAROLINA = 'bcbsnorthcarolina' as const;
export const BNSF = 'bnsf' as const;
export const CHILDRENSTEXAS = 'childrenstexas' as const;
export const CONTINENTAL_RESOURCES = 'clr' as const;
export const CONVIVA = 'conviva' as const;
export const COVENANTHEALTH = 'covenanthealth' as const;
export const CUSTOMER_DEMO = 'customerdemo' as const;
export const DEMO = 'demo' as const;
export const DOLLAR_GENERAL = 'dollarGeneral' as const;
export const DOLLAR_GENERAL_SUPPORT = 'dollarGeneralSupport' as const;
export const EMBOLD = 'embold' as const;
export const EMSANA_CARE = 'emsanaCare' as const;
export const ENSEMBLE = 'ensemble' as const;
export const ENSEMBLE_PROVIDER_LOOKUP = 'ensembleproviderlookup' as const;
export const EBAY = 'ebay' as const;
export const ICUBA = 'icuba' as const;
export const JBHUNT = 'jbhunt' as const;
export const JBHUNT_NATIONAL = 'jbhunt-national' as const;
export const JPMC = 'jpmc' as const;
export const JPMC_PROVIDER_LOOKUP = 'jpmcproviderlookup' as const;
export const LIFEPOINT = 'lifepoint' as const;
export const MICROSOFT = 'microsoft' as const;
export const NORTHROPGRUMMAN = 'northropgrumman' as const;
export const POLK = 'polk' as const;
export const QUANTUM = 'quantum' as const;
export const QUANTUM_DEMO = 'quantumdemo' as const;
export const QUANTUM_SANDBOX = 'quantumsandbox' as const;
export const REFERRAL_GUIDE_DEMO = 'referralguidedemo' as const;
export const ROPESGRAY = 'ropesgray' as const;
export const SANDBOX = 'sandbox' as const;
export const SOCT = 'soct' as const;
export const SOCT_PROVIDER_LOOKUP = 'soctproviderlookup' as const;
export const STANLEY_BLACK_AND_DECKER = 'stanleyblackanddecker' as const;
export const SURGERY_PLUS = 'surgeryPlus' as const;
export const CIGNADEMO = 'cignademo' as const;
export const TARGET = 'target' as const;
export const HOUSTON_METHODIST = 'houstonmethodist' as const;
export const WALMART = 'walmart' as const;
export const WALMART_NATIONAL = 'walmartnational' as const;

/** A Front End defined client identifier. No guarantee that it will have a matching backend value. */
export type EmboldClientIdentifier =
  | typeof ACME
  | typeof AMAZON
  | typeof AXA
  | typeof BCBS_NORTH_CAROLINA
  | typeof BNSF
  | typeof CHILDRENSTEXAS
  | typeof CONTINENTAL_RESOURCES
  | typeof CONVIVA
  | typeof COVENANTHEALTH
  | typeof CUSTOMER_DEMO
  | typeof DEMO
  | typeof DOLLAR_GENERAL
  | typeof DOLLAR_GENERAL_SUPPORT
  | typeof EMBOLD
  | typeof EMSANA_CARE
  | typeof ENSEMBLE
  | typeof ENSEMBLE_PROVIDER_LOOKUP
  | typeof EBAY
  | typeof ICUBA
  | typeof JBHUNT
  | typeof JBHUNT_NATIONAL
  | typeof JPMC
  | typeof JPMC_PROVIDER_LOOKUP
  | typeof LIFEPOINT
  | typeof MICROSOFT
  | typeof NORTHROPGRUMMAN
  | typeof POLK
  | typeof QUANTUM
  | typeof QUANTUM_DEMO
  | typeof QUANTUM_SANDBOX
  | typeof REFERRAL_GUIDE_DEMO
  | typeof ROPESGRAY
  | typeof SANDBOX
  | typeof SOCT
  | typeof SOCT_PROVIDER_LOOKUP
  | typeof STANLEY_BLACK_AND_DECKER
  | typeof SURGERY_PLUS
  | typeof CIGNADEMO
  | typeof TARGET
  | typeof HOUSTON_METHODIST
  | typeof WALMART
  | typeof WALMART_NATIONAL;

/* ******************************
 * Result types
 ****************************** */
export const PROVIDER_RESULT_TYPE: ProviderResultType = 'provider';
export const PLACE_RESULT_TYPE: PlaceResultType = 'place';
export const PROVIDER_TYPE: ProviderResultType = PROVIDER_RESULT_TYPE; // TODO Refactor to remove this duplicate constant
export const PLACE_TYPE: PlaceResultType = PLACE_RESULT_TYPE; // TODO Refactor to remove this duplicate constant

/* Generic numeric constants */
export const DEFAULT_RADIUS: number = 25;
export const PERCENTILE_THRESHOLD: number = 0.75;

/* ******************************
 * Network Slug References
 ****************************** */

export const HEALTH_SAVINGS_PLAN_PREFIX = 'MSJ' as const;
export const HEALTH_CONNECT_PLAN_PREFIX = 'YMJ' as const;
export const HEALTH_CONNECT = 'health_connect' as const;
export const EXTENDED_NETWORK = 'extended_network' as const;
export const SUR = 'sur' as const;
export const MERCY_HEALTH = 'unitedhealthcaremercyhealthwmt' as const;

/** Local storage version numbers */
export const LOCAL_STORAGE_VERSION_NUMBER: number = 1;
export const INTRO_MODAL_VERSION: number = 1;
export const LOGIN_NETWORK_NAME_VERSION: number = 1;

/* ******************************
 * Result sort options
 ****************************** */
interface SortOption {
  name: string;
  value: string;
  reverseValue?: string;
  label: string;
}

type SortOptions = Record<ResultType, Record<string, SortOption>>;

export const SORT_OPTIONS: SortOptions = {
  [PROVIDER_RESULT_TYPE]: {
    'Best Match': {
      name: 'sort by best match',
      value: 'best_match',
      label: 'Best Match',
    },
    Distance: {
      name: 'sort by distance',
      value: 'distance',
      label: 'Distance',
    },
    'Last Name (A-Z)': {
      name: 'sort by last name',
      value: 'last_name,first_name',
      reverseValue: '-last_name,-first_name',
      label: 'Last Name (A-Z)',
    },
    'Last Name (Z-A)': {
      name: 'sort by last name',
      value: '-last_name,-first_name',
      reverseValue: 'last_name,first_name',
      label: 'Last Name (Z-A)',
    },
  },
  [PLACE_RESULT_TYPE]: {
    Distance: {
      name: 'sort by distance',
      value: 'distance',
      label: 'Distance',
    },
    Alphabetical: {
      name: 'sort alphabetically',
      value: 'name',
      reverseValue: '-name',
      label: 'Alphabetical',
    },
    HospitalSafetyGrade: {
      name: 'sort by hospital safety grade',
      value: 'hospital_grade,distance',
      label: 'Hospital Safety Grade',
    },
  },
};

export const PROVIDER_SORT_OPTION_VALUES = {
  BEST_MATCH: 'best_match',
  COST_EFFICIENCY: '-score_care_journey_cost,-scoring_order,distance',
  DISTANCE: 'distance',
  LAST_NAME_AZ: 'last_name,first_name',
  LAST_NAME_ZA: '-last_name,-first_name',
} as const;
export type ProviderSortOptionValue =
  (typeof PROVIDER_SORT_OPTION_VALUES)[keyof typeof PROVIDER_SORT_OPTION_VALUES];
export const DEFAULT_PROVIDER_SORT: ProviderSortOptionValue =
  PROVIDER_SORT_OPTION_VALUES.BEST_MATCH;
export const PLACE_SORT_OPTION_VALUES = {
  DISTANCE: 'distance',
  NAME: 'name',
  NAME_REVERSE: '-name',
  HOSPITAL_GRADE: 'hospital_grade,distance',
} as const;
export type PlaceSortOptionValue =
  (typeof PLACE_SORT_OPTION_VALUES)[keyof typeof PLACE_SORT_OPTION_VALUES];
export const DEFAULT_PLACE_SORT: PlaceSortOptionValue = PLACE_SORT_OPTION_VALUES.DISTANCE;
// Unfortunately needing to basically duplicate this object in order
// to add Cost Efficiency with the correct ordering. Remove once Cost
// is turned on for all clients
export const SORT_OPTIONS_WITH_COST: SortOptions = {
  [PROVIDER_RESULT_TYPE]: {
    'Best Match': {
      name: 'sort by best match',
      value: PROVIDER_SORT_OPTION_VALUES.BEST_MATCH,
      label: 'Best Match',
    },
    'Cost Efficiency': {
      name: 'sort by cost efficiency',
      value: PROVIDER_SORT_OPTION_VALUES.COST_EFFICIENCY,
      label: 'Cost Efficiency',
    },
    Distance: {
      name: 'sort by distance',
      value: PROVIDER_SORT_OPTION_VALUES.DISTANCE,
      label: 'Distance',
    },
    'Last Name (A-Z)': {
      name: 'sort by last name',
      value: PROVIDER_SORT_OPTION_VALUES.LAST_NAME_AZ,
      reverseValue: PROVIDER_SORT_OPTION_VALUES.LAST_NAME_ZA,
      label: 'Last Name (A-Z)',
    },
    'Last Name (Z-A)': {
      name: 'sort by last name',
      value: PROVIDER_SORT_OPTION_VALUES.LAST_NAME_ZA,
      reverseValue: PROVIDER_SORT_OPTION_VALUES.LAST_NAME_AZ,
      label: 'Last Name (Z-A)',
    },
  },
  [PLACE_RESULT_TYPE]: {
    Distance: {
      name: 'sort by distance',
      value: PLACE_SORT_OPTION_VALUES.DISTANCE,
      label: 'Distance',
    },
    Alphabetical: {
      name: 'sort alphabetically',
      value: PLACE_SORT_OPTION_VALUES.NAME,
      reverseValue: PLACE_SORT_OPTION_VALUES.NAME_REVERSE,
      label: 'Alphabetical',
    },
    HospitalSafetyGrade: {
      name: 'sort by hospital safety grade',
      value: PLACE_SORT_OPTION_VALUES.HOSPITAL_GRADE,
      label: 'Hospital Safety Grade',
    },
  },
};

export const PLACE_BEST_MATCH_SORT: SortOption = {
  name: 'sort by best match',
  value: 'client_featured,distance',
  label: 'Best Match',
};

interface SectionContent {
  title: string;
  IconComponent: ComponentType<SvgIconProps>;
  description: string;
  about?: string;
  altText?: string;
}

export interface CommonSearchSuggestion {
  name: string;
  specialtyId?: number;
  subspecialtyId?: number;
  emboldId?: string;
  category?: 'term' | 'type' | 'focus_area';
  serviceId?: number;
  type?: ResultType;
}

interface CareCategoryOption {
  label: string;
  value?: string;
  IconComponent?: ComponentType<SvgIconProps>;
  shortName?: string;
  searchText?: string;
  accessibleLabel?: string | null;
  commonSearches?: CommonSearchSuggestion[];
  requiresSelection?: boolean;
  analyticsCategory: string;
}

export const PLACES_SUBSPECIALTY_SECTION: SectionContent = {
  title: 'Focus Areas',
  IconComponent: FocusAreasColored,
  description:
    'Focus areas highlight important facility information like available services, conditions treated,  procedures performed, or other relevant designations.',
};

export const PLACES_CLASSIFICATION_TYPES_SECTION: SectionContent = {
  title: 'Types and Classifications',
  IconComponent: FacilityTypeColored,
  description:
    'Facility type refers to the category or classification of a healthcare location based on the kind of care or services it provides.',
};

export const DEFAULT_LOCATIONS: Record<string, DefaultLocation> = {
  Bentonville: {
    lat: 36.3729,
    long: -94.2088,
    city: 'Bentonville',
    state: 'AR',
  },
  Dallas: {
    lat: 32.7774686,
    long: -96.8042882,
    city: 'Dallas',
    state: 'TX',
  },
  Seattle: {
    lat: 47.6062095,
    long: -122.3320708,
    city: 'Seattle',
    state: 'WA',
  },
  Orlando: {
    lat: 28.4814029,
    long: -81.483093,
    city: 'Orlando',
    state: 'FL',
  },
  Lakeland: {
    lat: 28.0394654,
    long: -81.9498042,
    city: 'Lakeland',
    state: 'FL',
  },
  Nashville: {
    lat: 36.174465,
    long: -86.76796,
    city: 'Nashville',
    state: 'TN',
  },
  Bethesda: {
    lat: 38.9816714,
    long: -77.1535963,
    city: 'Bethesda',
    state: 'MD',
  },
  JunoBeach: {
    lat: 26.88387,
    long: -80.05972,
    city: 'Juno Beach',
    state: 'FL',
  },
  WestPalmBeach: {
    lat: 26.709723,
    long: -80.064163,
    city: 'West Palm Beach',
    state: 'FL',
  },
  Goodlettsville: {
    lat: 36.3231,
    long: -86.7133,
    city: 'Goodlettsville',
    state: 'TN',
  },
  Mesa: {
    lat: 33.4152,
    long: -111.8315,
    city: 'Mesa',
    state: 'AZ',
  },
  FortWorth: {
    lat: 32.749904,
    long: -97.330339,
    city: 'Fort Worth',
    state: 'TX',
  },
  Lowell: {
    lat: 36.25535,
    long: -94.13076,
    city: 'Lowell',
    state: 'AR',
  },
  Hartford: {
    lat: 41.765775,
    long: -72.673356,
    city: 'Hartford',
    state: 'CT',
  },
  OklahomaCity: {
    lat: 35.468494,
    long: -97.521264,
    city: 'Oklahoma City',
    state: 'OK',
  },
  Cincinnati: {
    lat: 39.103697,
    long: -84.513613,
    city: 'Cincinnati',
    state: 'OH',
  },
  Chicago: {
    lat: 41.883229,
    long: -87.632398,
    city: 'Chicago',
    state: 'IL',
  },
  Raleigh: {
    lat: 35.781303,
    long: -78.64168,
    city: 'Raleigh',
    state: 'NC',
  },
  Brentwood: {
    lat: 35.995546,
    long: -86.778895,
    city: 'Brentwood',
    state: 'TN',
  },
  ArlingtonVA: {
    lat: 38.878465,
    long: -77.102811,
    city: 'Arlington',
    state: 'VA',
  },
  SanJose: {
    lat: 37.334794,
    long: -121.888145,
    city: 'San Jose',
    state: 'CA',
  },
  Andover: {
    lat: 42.655245,
    long: -71.139666,
    city: 'Andover',
    state: 'MA',
  },
  NewBritain: {
    lat: 41.667528,
    long: -72.783437,
    city: 'New Britain',
    state: 'CT',
  },
  Boston: {
    lat: 42.358994,
    long: -71.058629,
    city: 'Boston',
    state: 'MA',
  },
  FallsChurch: {
    lat: 38.883934,
    long: -77.173697,
    city: 'Falls Church',
    state: 'VA',
  },
  Bloomfield: {
    lat: 41.830957,
    long: -72.737425,
    city: 'Bloomfield',
    state: 'CT',
  },
  Minneapolis: {
    lat: 44.977486,
    long: -93.264367,
    city: 'Minneapolis',
    state: 'MN',
  },
  Houston: {
    lat: 29.760803,
    long: -95.369506,
    city: 'Houston',
    state: 'TX',
  },
};

export const ATLAS_GEOSEARCH_ENTITY_TYPE = 'PostalCodeArea' as const;

/* ******************************
 * Login Form Variations
 ****************************** */
export const DROPDOWN = 'dropdown' as const;
export const DROPDOWN_AUTOCOMPLETE = 'dropdown_autocomplete' as const;
export const PASSWORD = 'password' as const;
export const CHECKMARK = 'checkmark' as const;
export const ENSEMBLE_FORM = 'ensemble_form' as const;
export const AUTO_LOGIN = 'auto_login' as const;
export type FormVariant =
  | typeof DROPDOWN
  | typeof DROPDOWN_AUTOCOMPLETE
  | typeof PASSWORD
  | typeof CHECKMARK
  | typeof ENSEMBLE_FORM
  | typeof AUTO_LOGIN;

export const APP_MAIN_TAG_ID = 'main' as const;

// search constants
export const PROVIDER_CARE_CATEGORIES = {
  PROVIDER_SPECIALTY: 'specialty',
  PROVIDER_NAME: 'provider',
  PROVIDER_SERVICE: 'provider_service',
  PROVIDER_AFFILIATION: 'provider_affiliation',
} as const;
export type ProviderCareCategory = keyof typeof PROVIDER_CARE_CATEGORIES;

export const PLACE_CARE_CATEGORIES = {
  FACILITY_TYPE: 'place_specialty',
  FACILITY_NAME: 'place',
  FACILITY_SERVICE: 'place_service',
} as const;
export type PlaceCareCategory = keyof typeof PLACE_CARE_CATEGORIES;

export const CARE_CATEGORIES = {
  ...PROVIDER_CARE_CATEGORIES,
  ...PLACE_CARE_CATEGORIES,
  /** @deprecated This property is deprecated in favor of PROVIDER_SERVICE and FACILITY_SERVICE. This can be deleted after March 2025. Repo search "delete-service-type" */
  SERVICE: 'service',
  PROVIDER_LOCATION: 'provider_location', // this is intentionally not a part of the PROVIDER_CARE_CATEGORIES object so that it is NOT available in autocomplete
};

export const CARE_CATEGORIES_MAP: Record<ResultType, readonly string[]> = {
  [PROVIDER_RESULT_TYPE]: Object.values(PROVIDER_CARE_CATEGORIES),
  [PLACE_RESULT_TYPE]: Object.values(PLACE_CARE_CATEGORIES),
};

export const PROVIDERS_BY_IDS = 'providers_by_ids' as const;
export const PLACES_BY_IDS = 'places_by_ids' as const;

export const COMMON_SEARCH_NAMES = {
  PRIMARY_CARE: 'Primary Care',
  CARDIOLOGY: 'Cardiology',
  PEDIATRICS: 'Pediatrics',
  DERMATOLOGY: 'Dermatology',
  ORTHO_JOINT: 'Ortho-Joint',
  GASTROENTEROLOGY: 'Gastroenterology',
  PHYSICAL_THERAPY: 'Physical Therapy',
  URGENT_CARE: 'Urgent Care Clinc',
  HOSPITAL: 'Hospital',
  LAB: 'Laboratory',
  AMBULATORY_SURGICAL_CENTER: 'Ambulatory Surgical Center',
  COLONOSCOPY: 'Colonoscopy',
  ALLERGY_TEST: 'Allergy Test',
  GASTRIC_BYPASS: 'Gastric Bypass',
  CARDIAC_STRESS_TEST: 'Cardiac Stress Test',
  ACL_RECONSTRUCTION: 'ACL Reconstruction',
  SKIN_BIOPSY: 'Skin Biopsy',
} as const;

export const URGENT_CARE_SUGGESTION: CommonSearchSuggestion = process.env
  .REACT_APP_USE_FACILITY_CLASSIFICATION
  ? {
      name: COMMON_SEARCH_NAMES.URGENT_CARE,
      emboldId: '-6617717331067166059',
      category: 'focus_area',
      type: PLACE_RESULT_TYPE,
    }
  : {
      name: COMMON_SEARCH_NAMES.URGENT_CARE,
      specialtyId: 325,
      subspecialtyId: 590,
      type: PLACE_RESULT_TYPE,
    };

const FACILITY_TYPE_SEARCHES: CommonSearchSuggestion[] = process.env
  .REACT_APP_USE_FACILITY_CLASSIFICATION
  ? [
      URGENT_CARE_SUGGESTION,
      {
        name: COMMON_SEARCH_NAMES.HOSPITAL,
        emboldId: '345806208860093677',
        type: PLACE_RESULT_TYPE,
        category: 'type',
      },
      {
        name: COMMON_SEARCH_NAMES.LAB,
        emboldId: '-3282118282363561067',
        category: 'term',
        type: PLACE_RESULT_TYPE,
      },
      {
        name: COMMON_SEARCH_NAMES.AMBULATORY_SURGICAL_CENTER,
        emboldId: '-5450090132585410428',
        type: PLACE_RESULT_TYPE,
        category: 'type',
      },
    ]
  : [
      URGENT_CARE_SUGGESTION,
      {
        name: COMMON_SEARCH_NAMES.HOSPITAL,
        specialtyId: 302,
      },
      {
        name: COMMON_SEARCH_NAMES.LAB,
        specialtyId: 388,
      },
      {
        name: COMMON_SEARCH_NAMES.AMBULATORY_SURGICAL_CENTER,
        specialtyId: 325,
        subspecialtyId: 540,
      },
    ];

export const CARE_CATEGORY_OPTIONS: Record<string, CareCategoryOption> = {
  [CARE_CATEGORIES.PROVIDER_SPECIALTY]: {
    label: 'Provider by Specialty/Focus Area',
    value: CARE_CATEGORIES.PROVIDER_SPECIALTY,
    IconComponent: LocalHospitalOutlinedIcon,
    shortName: 'Specialty',
    searchText: 'e.g. Cardiology, Pediatrics...',
    accessibleLabel: 'Search for providers by specialty or focus area',
    commonSearches: [
      { name: COMMON_SEARCH_NAMES.PRIMARY_CARE, specialtyId: 5 },
      { name: COMMON_SEARCH_NAMES.CARDIOLOGY, specialtyId: 2 },
      { name: COMMON_SEARCH_NAMES.PEDIATRICS, specialtyId: 10 },
      { name: COMMON_SEARCH_NAMES.DERMATOLOGY, specialtyId: 11 },
      { name: COMMON_SEARCH_NAMES.ORTHO_JOINT, specialtyId: 3 },
      { name: COMMON_SEARCH_NAMES.GASTROENTEROLOGY, specialtyId: 6 },
      { name: COMMON_SEARCH_NAMES.PHYSICAL_THERAPY, specialtyId: 2041 },
    ],
    requiresSelection: true,
    analyticsCategory: 'specialty search',
  },
  [CARE_CATEGORIES.PROVIDER_NAME]: {
    label: 'Provider by Name/NPI',
    value: CARE_CATEGORIES.PROVIDER_NAME,
    IconComponent: ProviderName,
    shortName: 'Provider',
    searchText: 'Search for a provider by name or NPI',
    accessibleLabel: null, // searchText already provides sufficient screen-reader context
    commonSearches: [],
    requiresSelection: false,
    analyticsCategory: 'name search',
  },
  [CARE_CATEGORIES.PROVIDER_AFFILIATION]: {
    label: 'Provider by Affiliation',
    value: CARE_CATEGORIES.PROVIDER_AFFILIATION,
    IconComponent: GroupIcon,
    commonSearches: [],
    analyticsCategory: 'affiliation search',
  },
  // TODO: Uncomment when facility specialization search is upgraded in fusion
  [CARE_CATEGORIES.FACILITY_TYPE]: {
    label: 'Facility by Type',
    value: CARE_CATEGORIES.FACILITY_TYPE,
    IconComponent: Place,
    shortName: 'Facility',
    searchText: 'e.g. Hospital, Urgent Care...',
    accessibleLabel: 'Search for a facility by type e.g. Hospital, Urgent Care...',
    commonSearches: FACILITY_TYPE_SEARCHES,
    requiresSelection: true,
    analyticsCategory: 'facility search',
  },
  [CARE_CATEGORIES.FACILITY_NAME]: {
    label: 'Facility by Name',
    value: CARE_CATEGORIES.FACILITY_NAME,
    IconComponent: Place,
    shortName: 'Facility',
    searchText: 'Search for a facility by name',
    accessibleLabel: null, // searchText already provides sufficient screen-reader context
    commonSearches: [],
    requiresSelection: false,
    analyticsCategory: 'facility name search',
  },
  [CARE_CATEGORIES.PROVIDER_SERVICE]: {
    label: 'Service',
    value: CARE_CATEGORIES.PROVIDER_SERVICE,
    IconComponent: Service,
    shortName: 'Service',
    searchText: 'e.g. Allergy Test, Colonoscopy...',
    accessibleLabel: 'Search for a service',
    commonSearches: [
      { name: COMMON_SEARCH_NAMES.COLONOSCOPY, serviceId: 51 },
      { name: COMMON_SEARCH_NAMES.ALLERGY_TEST, serviceId: 12 },
      { name: COMMON_SEARCH_NAMES.GASTRIC_BYPASS, serviceId: 84 },
      { name: COMMON_SEARCH_NAMES.CARDIAC_STRESS_TEST, serviceId: 37 },
      { name: COMMON_SEARCH_NAMES.ACL_RECONSTRUCTION, serviceId: 6 },
      { name: COMMON_SEARCH_NAMES.SKIN_BIOPSY, serviceId: 199 },
    ],
    requiresSelection: true,
    analyticsCategory: 'service search',
  },
  [CARE_CATEGORIES.FACILITY_SERVICE]: {
    label: 'Service',
    value: CARE_CATEGORIES.FACILITY_SERVICE,
    IconComponent: Service,
    shortName: 'Service',
    searchText: 'e.g. Allergy Test, Colonoscopy...',
    accessibleLabel: 'Search for a service',
    commonSearches: [{ name: 'Knee Ankle MRI', serviceId: 128 }],
    requiresSelection: true,
    analyticsCategory: 'service search',
  },
  [CARE_CATEGORIES.PROVIDER_LOCATION]: {
    label: 'Provider by Facility',
    analyticsCategory: 'provider by facility search',
  },
};

export const HIGH_PERFORMING_TITLE = 'Highly Rated' as const;
export const HIGH_PERFORMING_TOOLTIP_TEXT =
  'This provider has been reviewed by medical experts and has proven to consistently deliver care that is among the best in the area in this specialty.';

export const DEFAULT_SUPPORT_MAILBOX = 'support@emboldhealth.com' as const;

/** Score tiers */
export const SCORE_TIER_HIGH = 1 as const;
export const SCORE_TIER_MIDDLE = 2 as const;
export const SCORE_TIER_LOW = 3 as const;
export const SCORE_TIER_UNSCORED = 0 as const;
export type ScoreTier =
  | typeof SCORE_TIER_HIGH
  | typeof SCORE_TIER_MIDDLE
  | typeof SCORE_TIER_LOW
  | typeof SCORE_TIER_UNSCORED;
export const VALID_SCORE_TIERS: readonly ScoreTier[] = [
  SCORE_TIER_HIGH,
  SCORE_TIER_MIDDLE,
  SCORE_TIER_LOW,
  SCORE_TIER_UNSCORED,
] as const;

/** Valid score tiers without the unscored tier (0) */
type ScoredTier = Exclude<ScoreTier, typeof SCORE_TIER_UNSCORED>;

export const SCORE_TIER_ICON_COMPONENTS: Record<ScoredTier, ComponentType<SvgIconProps>> = {
  [SCORE_TIER_HIGH]: StarsIcon,
  [SCORE_TIER_MIDDLE]: CheckCircleIcon,
  [SCORE_TIER_LOW]: ErrorIcon,
};

export const SCORE_TIER_LANGUAGE: Record<ScoredTier, string> = {
  [SCORE_TIER_HIGH]: 'Exceeds Standards',
  [SCORE_TIER_MIDDLE]: 'Meets Standards',
  [SCORE_TIER_LOW]: 'Below Standards',
};

export const SCORE_TIER_ICON_ALT_TEXT: Record<ScoredTier, string> = {
  // this will be used as the titleAccess prop on the score tier icon
  ...SCORE_TIER_LANGUAGE,
};

export const SCORE_COLORS_BY_TIER: Record<ScoredTier, string> = {
  [SCORE_TIER_HIGH]: 'scoreTier.1.dark',
  [SCORE_TIER_MIDDLE]: 'scoreTier.2.dark',
  [SCORE_TIER_LOW]: 'scoreTier.3.dark',
};

export const EXTERNAL_QUALITY_PRIMARY_SCORE_COLORS: Record<ScoredTier, string> = {
  [SCORE_TIER_HIGH]: 'success.main',
  [SCORE_TIER_MIDDLE]: 'secondary.main',
  [SCORE_TIER_LOW]: 'primary.main',
};

export const EXTERNAL_QUALITY_PRIMARY_SCORE_LABELS: Record<ScoredTier, string> = {
  [SCORE_TIER_HIGH]: 'Highest Value',
  [SCORE_TIER_MIDDLE]: 'High Value',
  [SCORE_TIER_LOW]: 'Average',
};

export const EXTERNAL_QUALITY_PRIMARY_SCORE_DESCRIPTIONS: Record<ScoredTier, string> = {
  [SCORE_TIER_HIGH]:
    'This rating is based on a composite score of cost, quality, and appropriateness. Generally, providers who receive this rating perform well above average compared to their peers.',
  [SCORE_TIER_MIDDLE]:
    'This rating is based on a composite score of cost, quality, and appropriateness. Generally, providers who receive this rating perform slightly better than average compared to their peers.',
  [SCORE_TIER_LOW]:
    'This rating is based on a composite score of cost, quality, and appropriateness. Generally, providers who receive this rating perform about equal to their peers.',
};

export const EXTERNAL_QUALITY_PRIMARY_SCORE_ICONS: Record<
  ScoredTier,
  ComponentType<SvgIconProps>
> = {
  [SCORE_TIER_HIGH]: VerifiedUser,
  [SCORE_TIER_MIDDLE]: StarsIcon,
  [SCORE_TIER_LOW]: CheckCircleIcon,
};

export const SUBSPECIALTY_SECTION: SectionContent = {
  title: 'Focus Areas',
  IconComponent: FocusAreasColored,
  description:
    'The following highlights the demonstrated capabilities of a healthcare provider, determined by the quality of their practice according to standards identified by medical experts, the volume of their cases, or the areas of expertise they have self-identified.',
  about:
    'Focus Areas provide a closer look at the medical conditions and clinical procedures a provider specializes in, the volume of cases managed, and self-identified areas of expertise.',
};

// TODO: Add wording to description and about
export const SERVICE_COST_INFO: SectionContent = {
  title: 'Estimated Costs at this Location',
  IconComponent: MonetizationOnIcon,
  description:
    'The price displayed is an estimate of this service only. In some cases additional services may be provided and these will change the total price. Always check with your provider and health plan for more detailed information.',
};

export const HEALTH_AND_WELLNESS: SectionContent = {
  title: 'Health &\nWellness Programs',
  IconComponent: HealthAndWellness,
  description:
    "When they relate to your search, your employer's benefits display above the results, reminding you of effective, low-cost options for specific medical needs.",
  altText: 'Example benefit rating showing a score of 3 out of 4 stars',
};

export const STRENGTHS_SECTION: SectionContent = {
  title: 'Strengths',
  IconComponent: StrengthsColored,
  description:
    "Provider Strengths highlight a doctor's areas of exceptional care in comparison to their peers.",
};

export const SUBSPECIALTY_TYPES = {
  SCORED: 'scored',
  HIGH_VOLUME: 'high_volume',
  SELF_IDENTIFIED: 'self_identified',
  EXTERNAL_SCORE: 'external_score',
} as const;
export type SubspecialtyType = (typeof SUBSPECIALTY_TYPES)[keyof typeof SUBSPECIALTY_TYPES];

export const COST_EFFICIENCY_SECTION: SectionContent = {
  title: 'Cost Efficiency',
  IconComponent: CostEfficiencyColored,
  description:
    'Embold recommends that you first look for a high-quality provider and then consider their Cost Efficiency. High-quality and highly cost-efficient providers deliver the best care while using the least resources.',
};

interface ServiceCostContent {
  buttonText: string;
  IconComponent: ComponentType<SvgIconProps>;
  description: string;
}

export const SERVICE_COST_CONTENT: ServiceCostContent = {
  buttonText: 'See estimate >',
  IconComponent: CostEfficiencyColored,
  description: 'Cost estimates may vary by location.',
};

export const EXTERNAL_FOCUS_AREAS_SECTION: SectionContent = {
  title: 'BCBS Clinical Expertise',
  IconComponent: FocusAreasColored,
  description:
    'The following highlights the demonstrated areas of expertise for a healthcare provider based on a clinical review of claims data for the past 2 years.',
};

export const EXTERNAL_COST_QUALITY_SECTION: SectionContent = {
  title: 'BCBS Cost Information',
  IconComponent: CostEfficiencyColored,
  description:
    "A specialist's cost score reflects the total cost of care a doctor or practice provides during a full episode of treatment — not just a single visit. For example, a routine check-up won't significantly impact how much money is spent. But if a patient needs surgery or ongoing specialty care for a specific condition, that's where meaningful cost differences can exist.",
};

export const CHOOSE_NETWORK_TEXT = 'Choose Your Network' as const;

export const COST_EFFICIENCY_MAP: Record<1 | 2 | 3, string> = {
  1: 'Less Efficient',
  2: 'Average',
  3: 'More Efficient',
};

export const CHAT_BUTTON_Z_INDEX: number = 8;
export const MAP_LOADING_WRAPPER_Z_INDEX: number = CHAT_BUTTON_Z_INDEX - 2;
export const MAP_LOADING_MODAL_Z_INDEX: number = CHAT_BUTTON_Z_INDEX - 1;
export const DRAWER_Z_INDEX: number = 1600;

export const PROVIDERS_APPENDIX_ROUTE = '/specialties/provider' as const;
export const PLACES_APPENDIX_ROUTE = '/specialties/place' as const;
export const EMBOLD_MEASURING_WHAT_MATTERS_PDF_URL =
  'https://content.emboldhealth.com/external/measuring_what_matters_ebook_2024.pdf' as const;

export const BLUES_HOSPITAL_AFFILIATIONS_EXPLAINER_TEXT =
  'For your network, "Hospital affiliation" refers exclusively to hospitals where the providers has admitting privileges.' as const;

/**
 * Featured actions are objects stored in the network config.
 * Each object will have a "variant" key with a value that is one of the values
 * contained in this constant object.
 */
export const FEATURED_ACTION_VARIANTS: Record<string, FeaturedActionVariant> = {
  PROMOTION: 'P',
  SEARCH: 'S',
};

export const DEFAULT_VALID_SCORE_TIERS: readonly ScoreTier[] = [1, 2, 3] as const;

interface LocationHourSchedule {
  day: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  order: number;
  openingTime: string;
  closingTime: string;
}

export const DAYS_OF_THE_WEEK: Record<DayOfTheWeekAbbreviation, LocationHourSchedule> = {
  mon: { day: 'Monday', order: 1, openingTime: 'Closed', closingTime: 'Closed' },
  tue: { day: 'Tuesday', order: 2, openingTime: 'Closed', closingTime: 'Closed' },
  wed: { day: 'Wednesday', order: 3, openingTime: 'Closed', closingTime: 'Closed' },
  thu: { day: 'Thursday', order: 4, openingTime: 'Closed', closingTime: 'Closed' },
  fri: { day: 'Friday', order: 5, openingTime: 'Closed', closingTime: 'Closed' },
  sat: { day: 'Saturday', order: 6, openingTime: 'Closed', closingTime: 'Closed' },
  sun: { day: 'Sunday', order: 7, openingTime: 'Closed', closingTime: 'Closed' },
};

export const TERMS_AND_CONDITIONS_LAST_UPDATED: Date = new Date('FEBRUARY 23, 2024');
